import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table';
import { useLocation } from 'react-router';

import { useForwardCatalog, useSpotCatalog } from '@/hooks/api/catalog';
import type { CatalogItem } from '@/pages/Catalog/types';
import { arrEqualsAll } from '@/pages/Catalog/utils/filters';
import { takeItemBasedOnOrder } from '@/pages/Catalog/utils/takeItemBasedOnOrder';
import getQueryStatus from '@/utils/getQueryStatus';
import { parseBackendData } from '@/utils/parseBackendData';

import {
  ADDITIONAL_ITEM,
  defaultVisibilityState,
  forwardCurvesColumns,
  spotColumns
} from './constants';

import type { OnChangeFn, VisibilityState } from '@tanstack/react-table';

const defaultColumn = {
  filterFn: arrEqualsAll
};
const useCatalog = (isForwardCurves = false) => {
  const location = useLocation();

  const columns = isForwardCurves ? forwardCurvesColumns : spotColumns;

  const [activeTab, setActiveTab] = useState<number>(isForwardCurves ? 1 : 0);
  const [showDiscontinued, setShowDiscontinued] = useState<boolean>(false);

  const [expandedData, setExpandedData] = useState<boolean>(false);
  const [filterOpen, setFilterOpen] = useState<boolean>(false);
  const [sorting, setSorting] = useState(isForwardCurves ? [] : [{ id: 'Alias', desc: false }]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(defaultVisibilityState);

  const spotCatalogQuery = useSpotCatalog();
  const forwardCatalogQuery = useForwardCatalog();

  // Prevent state updates before mount or after unmount
  const isMountedRef = useRef(false);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const spotStatus = getQueryStatus(spotCatalogQuery);
  const forwardStatus = getQueryStatus(forwardCatalogQuery);
  const status = isForwardCurves ? forwardStatus : spotStatus;
  const catalog = isForwardCurves ? forwardCatalogQuery.data : spotCatalogQuery.data;

  useEffect(() => {
    clearState();
    setActiveTab(isForwardCurves ? 1 : 0);
  }, [location.pathname]);

  const clearState = useCallback(() => {
    setFilterOpen(false);
    setColumnVisibility(prevState => ({
      ...prevState,
      TimeRef: isForwardCurves || expandedData,
      PeriodType: isForwardCurves || expandedData
    }));
  }, [isForwardCurves, expandedData]);

  const filteredData = useMemo(() => {
    if (!catalog) return [];
    const filteredByModules = parseBackendData(catalog);
    if (expandedData || isForwardCurves) return filteredByModules;

    return Object.values(
      filteredByModules.reduce((acc: { [code: string]: CatalogItem }, item) => {
        const existing = acc[item.Code];
        if (existing) {
          existing[ADDITIONAL_ITEM] = true;
          acc[item.Code] = takeItemBasedOnOrder(existing, item);
        } else {
          acc[item.Code] = item;
        }
        return acc;
      }, {})
    );
  }, [catalog, expandedData, isForwardCurves]);

  const safeSetColumnVisibility: OnChangeFn<VisibilityState> = updater => {
    if (!isMountedRef.current) return;
    setColumnVisibility(prev => (typeof updater === 'function' ? updater(prev) : updater));
  };
  const safeSetSorting: typeof setSorting = updater => {
    if (!isMountedRef.current) return;
    setSorting(prev => (typeof updater === 'function' ? updater(prev) : updater));
  };

  const table = useReactTable({
    data: filteredData,
    columns,
    defaultColumn,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getCoreRowModel: getCoreRowModel(),
    globalFilterFn: 'includesString',
    state: {
      columnVisibility,
      sorting
    },
    onColumnVisibilityChange: safeSetColumnVisibility,
    onSortingChange: safeSetSorting
  });

  return {
    status,
    filterOpen,
    setFilterOpen,
    filteredData,
    table,
    activeTab,
    setActiveTab,
    isForwardCurves,
    expandedData,
    setExpandedData,
    showDiscontinued,
    setShowDiscontinued
  };
};
export default useCatalog;
